import { useState, useEffect, useRef } from "react";
import { useNavigate } from "@remix-run/react";
import { useApi } from "@core/api";
import { useQuery } from "@tanstack/react-query";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes, faIdBadge } from "@fortawesome/pro-light-svg-icons";
import { useCurrentUser } from "@core/hooks/useCurrentUser";
import { clearSiteTopBannerHeight, setSiteTopBannerHeight } from "./siteTopBanners";

// Shows independently of the generic AccountCompletionBanner (which only ever
// surfaces one "next" step) — this is a dedicated, always-visible banner for the
// EPCS requirement specifically, so it isn't hidden behind other pending steps.
export default function EpcsAuthorizationBanner() {
  const api = useApi();
  const { user } = useCurrentUser();
  const navigate = useNavigate();
  const bannerRef = useRef<HTMLDivElement | null>(null);
  const [isDismissed, setIsDismissed] = useState(false);

  const dismissKey = user?.Id ? `epcsAuthorizationBannerDismissed:${user.Id}` : null;

  useEffect(() => {
    if (!dismissKey) { setIsDismissed(false); return; }
    setIsDismissed(localStorage.getItem(dismissKey) === "true");
  }, [dismissKey]);

  const { data: steps } = useQuery({
    queryKey: ["account-completion-steps", user?.Id],
    queryFn: () => api["/site/account-completion-steps"](),
    staleTime: 60_000,
    refetchOnWindowFocus: true,
    enabled: !!user?.Id,
  });

  const handleDismiss = () => {
    if (dismissKey) localStorage.setItem(dismissKey, "true");
    setIsDismissed(true);
  };

  const epcsStep = (steps ?? []).find(s => s.Id === "epcs-authorization" && s.IsEnabled);
  const show = !isDismissed && !!epcsStep && !epcsStep.IsCompleted;

  useEffect(() => {
    if (!show) {
      clearSiteTopBannerHeight("epcs");
      return;
    }
    const updateOffset = () => {
      setSiteTopBannerHeight("epcs", bannerRef.current?.offsetHeight ?? 0);
    };
    updateOffset();
    const observer = new ResizeObserver(updateOffset);
    if (bannerRef.current) observer.observe(bannerRef.current);
    window.addEventListener("resize", updateOffset);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", updateOffset);
      clearSiteTopBannerHeight("epcs");
    };
  }, [show]);

  if (!show) return null;

  return (
    <div
      ref={bannerRef}
      data-testid="epcs-authorization-banner"
      className="fixed left-0 right-0 flex items-center justify-between px-4 py-1.5 print:hidden"
      style={{ top: "calc(var(--site-top-banner-below-onboarding, 0px) + var(--site-top-banner-announcement-height, 0px))", zIndex: 248, background: "#3a2a0a", borderBottom: "1px solid #4a3a1a" }}
    >
      <span className="flex items-center gap-2 text-sm font-semibold tracking-widest uppercase" style={{ color: "#D4AF37", fontFamily: "'Outfit', sans-serif", letterSpacing: "0.12em" }}>
        <FontAwesomeIcon icon={faIdBadge} />
        DEA EPCS
      </span>
      <span className="mx-3 text-sm" style={{ color: "rgba(255,255,255,0.7)", fontFamily: "'Outfit', sans-serif" }}>
        Proceed to account settings to complete your DEA EPCS authorization.
      </span>
      <div className="flex items-center gap-2 ml-auto">
        <button
          type="button"
          onClick={() => navigate(epcsStep.Route)}
          className="px-3 py-1 rounded text-xs font-semibold"
          style={{ background: "#D4AF37", color: "#0F2942", border: "none", fontFamily: "'Outfit', sans-serif" }}
        >
          Go
        </button>
        <button
          type="button"
          onClick={handleDismiss}
          className="p-1"
          style={{ color: "rgba(255,255,255,0.5)" }}
          aria-label="Dismiss"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    </div>
  );
}
